import { create } from 'zustand';
import { JWT_STORAGE_KEY } from '../services/authStorageKeys';
import { CSRF_COOKIE_NAME, isAuthEnabled } from '../services/sessionCookieNames';

/**
 * Auth state.
 *
 * `isAuthenticated` is hydrated synchronously at boot from the JS-readable
 * sentinel — the per-app CSRF cookie (`csrf_admin_token`) in cookie mode, the
 * JWT in localStorage mode — so the shell can paint before whoami returns.
 *
 * `sessionStatus` is the admin addition. Core only ever needed "is there a
 * session"; admin also needs "is there an ADMIN profile behind it". A
 * signed-in user whose account has no company-persona profile must land on a
 * clear refusal, not on a spinner that waits for a profile that never comes.
 *
 *   idle      — no session (signed out)
 *   restoring — session sentinel present, whoami in flight
 *   ready     — company profile loaded (capabilities decide what it can see)
 *   no-access — signed in, but the account has no company profile
 *   error     — whoami failed transiently (network / 5xx); retryable
 */
export type SessionStatus = 'idle' | 'restoring' | 'ready' | 'no-access' | 'error';

interface AuthState {
  isAuthenticated: boolean;
  sessionStatus: SessionStatus;
  setAuthenticated: (value: boolean) => void;
  setSessionStatus: (value: SessionStatus) => void;
}

const csrfCookieRegex = new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=`);

function detectInitialAuth(): boolean {
  if (!isAuthEnabled) return false;
  if (typeof document !== 'undefined' && csrfCookieRegex.test(document.cookie)) return true;
  if (typeof localStorage !== 'undefined') return localStorage.getItem(JWT_STORAGE_KEY) != null;
  return false;
}

const initialAuth = detectInitialAuth();

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: initialAuth,
  sessionStatus: initialAuth ? 'restoring' : 'idle',
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setSessionStatus: (sessionStatus) => set({ sessionStatus }),
}));
